'use client';

import { useRef, useEffect, useState } from 'react';

import { ScrollingTagsProps, TagProps } from './types';

import { classNames } from '@/shared/lib/classNames/classNames';

export const ScrollingTags = ({
  tags,
  direction = 'left',
  speed = 10,
  className,
}: ScrollingTagsProps) => {
  const [duration, setDuration] = useState(10);
  const [isReady, setIsReady] = useState(false);
  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const idRef = useRef(`scrl-${Math.random().toString(36).substring(2, 10)}`);
  const componentId = idRef.current;

  useEffect(() => {
    if (!contentRef.current || tags.length === 0) {
      return;
    }

    const timer = setTimeout(() => {
      if (!contentRef.current) {
        return;
      }

      const contentWidth = contentRef.current.offsetWidth;
      const pixelsPerSecond = speed * 10;
      const calculatedDuration = contentWidth / pixelsPerSecond;

      setDuration(calculatedDuration);
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [tags, speed]);

  const leftAnimationName = `scrollLeft-${componentId}`;
  const rightAnimationName = `scrollRight-${componentId}`;

  useEffect(() => {
    if (!tagsContainerRef.current || !isReady) {
      return;
    }

    const element = tagsContainerRef.current;

    const restartAnimation = () => {
      element.style.animation = 'none';
      const animationName =
        direction === 'left' ? leftAnimationName : rightAnimationName;
      element.style.animation = `${animationName} ${duration}s linear infinite`;
    };

    restartAnimation();
  }, [duration, direction, isReady, leftAnimationName, rightAnimationName]);

  const animationStyle = isReady
    ? {
        display: 'inline-flex',
        animationName:
          direction === 'left' ? leftAnimationName : rightAnimationName,
        animationDuration: `${duration}s`,
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
        animationFillMode: 'forwards',
        willChange: 'transform',
      }
    : {
        display: 'inline-flex',
      };

  const keyframesStyle = `
    @keyframes ${leftAnimationName} {
      0% { transform: translateX(0); }
      100% { transform: translateX(calc(-100% / 2)); }
    }
    
    @keyframes ${rightAnimationName} {
      0% { transform: translateX(calc(-100% / 2)); }
      100% { transform: translateX(0); }
    }
  `;

  const renderContent = () => (
    <div ref={contentRef} className="flex gap-4 px-2">
      {tags.map((tag, index) => (
        <Tag key={`tag-${index}-${componentId}`}>{tag}</Tag>
      ))}
    </div>
  );

  if (tags.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames({
        cls: 'w-full overflow-hidden',
        additional: [className],
      })}
      suppressHydrationWarning
    >
      <style
        dangerouslySetInnerHTML={{ __html: keyframesStyle }}
        suppressHydrationWarning
      />

      <div
        ref={tagsContainerRef}
        style={animationStyle}
        suppressHydrationWarning
      >
        <div className="flex w-[max-content]">
          {renderContent()}
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

const Tag = ({ children }: TagProps) => {
  return (
    <div
      className="text-text-base-secondary flex h-[64px] shrink-0 items-center justify-center rounded-[18px] px-8 text-[18px] leading-[24px] font-medium"
      style={{
        background: `linear-gradient(94deg, rgba(255, 255, 255, 0.0312) 0%, rgba(255, 255, 255, 0.0912) 100%)`,
      }}
    >
      {children}
    </div>
  );
};
